export interface RandomGenerator {
  next(): number;
  nextRange(min: number, max: number): number;
  nextInt(min: number, max: number): number;
  nextBoolean(): boolean;
  nextItem<T>(items: T[]): T;
}

export class SeededRandom implements RandomGenerator {
  private readonly seedValue: string;
  private state: number;

  constructor(seed?: string) {
    this.seedValue = seed ?? `${Date.now()}-${Math.random()}`;
    this.state = this.hashSeed(this.seedValue);
  }

  getSeed(): string {
    return this.seedValue;
  }

  private hashSeed(seed: string): number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash + char) | 0;
    }
    return hash >>> 0;
  }

  next(): number {
    // Linear Congruential Generator (LCG) - Park-Miller variant
    this.state = (this.state * 1664525 + 1013904223) % 4294967296;
    return this.state / 4294967296;
  }

  nextRange(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  nextInt(min: number, max: number): number {
    return Math.floor(this.nextRange(min, max));
  }

  nextBoolean(): boolean {
    return this.next() >= 0.5;
  }

  nextItem<T>(items: T[]): T {
    if (items.length === 0) {
      throw new Error('Cannot select item from empty array');
    }
    return items[Math.floor(this.next() * items.length)];
  }
}

export const defaultRandom: RandomGenerator = {
  next: () => Math.random(),
  nextRange: (min: number, max: number) => Math.random() * (max - min) + min,
  nextInt: (min: number, max: number) => Math.floor(Math.random() * (max - min)) + min,
  nextBoolean: () => Math.random() >= 0.5,
  nextItem: <T>(items: T[]) => {
    if (items.length === 0) {
      throw new Error('Cannot select item from empty array');
    }
    return items[Math.floor(Math.random() * items.length)];
  },
};

export const createRandomGenerator = (seed?: string): RandomGenerator => {
  return seed ? new SeededRandom(seed) : defaultRandom;
};
